<template>
  <div class="app">
    <loader v-if="isLoading" />

    <template v-else>
      <div class="app__inner">
        <div class="app__body">
          <component
            :is="component"
            :flow-name="view"
            :session="session"
            :country-code="countryCode"
            :theme="theme"
            :source-info="sourceInfo"
            :userType="userType"
            :isAllowedConstantFocus="isUkUser"
            :isPasskeyLoginFeatureEnabled="isPasskeyLoginFeatureEnabled"
            @success="onSessionStarted"
            @success-phone="onPhoneSessionStarted"
            @success-otp="onSuccessOtp"
            @success-otp-phone="onSuccessOtpPhone"
            @back="onBack"
            @change-flow="onChangeStep"
            @critical-error="onCriticalError"
            @too-many-error="onTooManyError"
            @invalid-credential-id-error="onInvalidCredentialIdError"
            @session-expired="onSessionExpired"
            @session-restart="onSessionRestart"
          >
            <template v-slot:user-type>
              <UserTypeTabs
                  v-if="isExistUserTabs"
                  :isDisabled="isDisabledTabs"
                  v-model="userType"
              />
            </template>
          </component>

          <ErrorPopup
            :show="isCriticalError"
            :is-too-many="isTooManyError"
            :is-timeout="isTimeoutError"
            :is-invalid-credential-id="isInvalidCredentialId"
            @close="onCloseErrorPopup"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import LoginEmail from "./views/LoginEmail";
import SignupEmail from "./views/SignupEmail";
import SalesFunnelEmail from "./views/SalesFunnelEmail";
import PhoneCommon from "./views/PhoneCommon";
import PhoneCorporate from "./views/PhoneCorporate";
import PhoneFunnel from "./views/PhoneFunnel";
import EmailOtp from "./views/EmailOtp";
import PhoneOtp from "./views/PhoneOtp";
import UkWarningMessage from "./views/UkWarningMessage";
import ErrorPopup from "./components/ErrorPopup";
import Loader from "./components/Loader/Loader";
import { isWebView } from "./services/web-view-service";

import AuthService from "./services/auth";
import AuthStorage from "./services/auth-storage";
import TokenService from "./services/token";
import PartnerService from "./services/partner";
import UserTypeTabs from "@paybis/frontend-authentication-app/src/components/UserTypeTabs/UserTypeTabs.vue";

import {
  AUTHENTICATED_EVENT,
  ERROR_EVENT,
  LOADED_EVENT,
} from "./constants";
import { Tokens } from "./models/tokens";
import UserLocaleStorage from "./services/user-locale-storage";
import Partner from "./models/partner";
import AuthClient from "./services/auth-client";

import eventBus from "./helpers/eventBus";
import { UserType, AuthType } from "@/types";

const LOGIN_STEP = "login";
const SIGNUP_STEP = "signup";
const FUNNEL_STEP = "funnel";
const EMAIL_OTP_STEP = "otp.email";
const PHONE_OTP_STEP = "otp.phone";
const PHONE_COMMON_STEP = "phone.common";
const PHONE_CORPORATE_STEP = "phone.corporate";
const PHONE_FUNNEL_STEP = "phone.funnel";
const WIDGET_FLOW = "widget";
const UK_WARNING_STEP = "uk.warning";

const AUTH_TYPE_TO_USER_TYPE_MAP = {
  [AuthType.PRIVATE]: UserType.PRIVATE,
  [AuthType.CORPORATE]: UserType.CORPORATE,
};

const STEPS_TO_COMPONENT_MAP = {
  [LOGIN_STEP]: LoginEmail,
  [SIGNUP_STEP]: SignupEmail,
  [FUNNEL_STEP]: SalesFunnelEmail,
  [EMAIL_OTP_STEP]: EmailOtp,
  [PHONE_COMMON_STEP]: PhoneCommon,
  [PHONE_CORPORATE_STEP]: PhoneCorporate,
  [PHONE_FUNNEL_STEP]: PhoneFunnel,
  [PHONE_OTP_STEP]: PhoneOtp,
  [WIDGET_FLOW]: SalesFunnelEmail,
  [UK_WARNING_STEP]: UkWarningMessage,
};

const AUTHORIZATION_FLOW_STEPS = [
  LOGIN_STEP,
  SIGNUP_STEP,
  FUNNEL_STEP,
];

const AVAILABLE_STEPS = [
  LOGIN_STEP,
  SIGNUP_STEP,
  FUNNEL_STEP,
  EMAIL_OTP_STEP,
  PHONE_COMMON_STEP,
  PHONE_FUNNEL_STEP,
  PHONE_OTP_STEP,
  WIDGET_FLOW,
  UK_WARNING_STEP,
];

export default {
  name: "Authorization",

  provide() {
    return {
      startAuthSession: this.startAuthSession,
      emailHintOverride: this.i18nOverrides && this.i18nOverrides.emailHint,
    };
  },

  components: {
    Loader,
    ErrorPopup,
    UserTypeTabs,
  },

  props: {
    view: {
      type: String,
      required: true,
      validator: (value) => AVAILABLE_STEPS.indexOf(value) !== -1,
    },

    countryCode: {
      type: String,
      required: false,
    },

    email: {
      type: String,
      required: false,
      default: "",
    },

    locale: {
      type: String,
      required: false,
      default: "en",
    },

    theme: {
      type: String,
      required: false,
    },

    sourceInfo: {
      type: Object,
      required: false,
      default: null,
    },

    i18nOverrides: {
      type: Object,
      required: false,
      default: null,
    },

    partnerId: {
      type: String,
      required: false,
      default: null,
    },

    showSignupWarning: {
      type: Boolean,
      required: true,
    },

    selectedAuthType: {
      type: AuthType,
      required: false,
      default: AuthType.PRIVATE,
    },

    isPasskeyLoginFeatureEnabled: {
      type: Boolean,
      required: false,
    },
  },

  data() {
    return {
      previous: null,
      step: this.view,
      session: null,
      criticalError: null,
      isTimeoutError: false,
      isTooManyError: false,
      isInvalidCredentialId: false,
      isLoading: true,
      userType: UserType.PRIVATE,
      authorizationFlow: this.view,
    };
  },

  computed: {
    isCriticalError() {
      return this.criticalError !== null;
    },
    component() {
      return STEPS_TO_COMPONENT_MAP[this.step];
    },
    isEmailProvided() {
      return String(this.email).length > 0;
    },
    isUkUser() {
      return ["UK", "GB"].includes(this.countryCode);
    },
    isUkUserWarning() {
      return this.showSignupWarning && this.isUkUser;
    },
    isInitializeFlow() {
      return this.isSignupFlow || this.isLoginFlow;
    },
    isExistUserTabs() {
      return this.isInitializeFlow;
    },
    isDisabledTabs() {
      return this.step !== SIGNUP_STEP && this.step !== LOGIN_STEP;
    },
    isSignupFlow() {
      return this.authorizationFlow === SIGNUP_STEP;
    },
    isLoginFlow() {
      return this.authorizationFlow === LOGIN_STEP;
    },
  },

  watch: {
    locale(newLocale, locale) {
      if (newLocale === locale) {
        return;
      }

      UserLocaleStorage.storeLocale(newLocale);
    },

    view(newView, previousView) {
      if (newView === previousView) {
        return;
      }

      this.step = newView;
    },

    step(newStep, previousStep) {
      if (newStep === previousStep) {
        return;
      }

      if (AUTHORIZATION_FLOW_STEPS.includes(newStep)) {
        this.authorizationFlow = newStep;
      }

      if (this.previous === newStep) {
        this.previous = null;
        this.$emit("change-step", newStep);

        return;
      }

      // Prevent ability to open entry view if email passed
      if (this.email && newStep === EMAIL_OTP_STEP) {
        return;
      }

      this.$emit("change-step", newStep, previousStep);
      this.previous = previousStep;
    },

    partnerId: {
      handler: (id) => PartnerService.setPartner(new Partner(id)),
      immediate: true,
    },
  },

  async created() {
    if (this.selectedAuthType && AUTH_TYPE_TO_USER_TYPE_MAP[this.selectedAuthType]) {
      this.userType = AUTH_TYPE_TO_USER_TYPE_MAP[this.selectedAuthType];
    }

    UserLocaleStorage.storeLocale(this.locale);
    if (isWebView() && this.hasExistingTokens()) {
      try {
        const tokens = await this.getExistingTokens();

        this.emitAuthTokens(tokens);
      } catch {
        await this.restartSessionWithLogout();
      }

      return;
    }

    if (AuthStorage.hasStoredSession()) {
      // Check case when session and provided email mismatch
      const isSessionValid = AuthStorage.verifySessionWithEmail(this.email);

      if (!isSessionValid) {
        await this.restartSessionWithLogout();
        return;
      }

      this.resumeStoredSession();
      return;
    }

    if (this.email === "") {
      this.makeApplicationLoaded();
      return;
    }

    this.startAuthSession(this.email, this.locale);
  },

  methods: {
    hasExistingTokens() {
      return TokenService.hasToken() || TokenService.hasRefreshToken();
    },

    getExistingTokens() {
      if (!TokenService.hasToken() || TokenService.isTokenExpired()) {
        return AuthClient.refresh(TokenService.getRefreshToken());
      }

      return new Tokens({
        jwt: TokenService.getToken(),
        jwtExpiresAt: TokenService.getTokenExpirationString(),
        refreshToken: TokenService.getRefreshToken(),
        refreshTokenExpiresAt: TokenService.getRefreshTokenExpirationString(),
      });
    },

    makeApplicationLoaded() {
      this.isLoading = false;
      eventBus.emit(LOADED_EVENT);
    },

    onSessionRestart(email) {
      this.startAuthSession(email);
    },

    onCloseErrorPopup() {
      this.criticalError = null;
      this.isTimeoutError = false;
      this.isTooManyError = false;
      this.isInvalidCredentialId = false;
    },

    onSessionExpired(error) {
      this.criticalError = error;
      this.step = this.view;
      this.isTimeoutError = true;
    },

    onCriticalError(error) {
      if (error.redirectLocation !== undefined) {
        this.isLoading = true;
        window.location.assign(error.redirectLocation);
        return;
      }
      this.criticalError = error;
      this.step = this.view;
      eventBus.emit(ERROR_EVENT, {
        error,
      });
    },

    onTooManyError(error) {
      this.isTooManyError = true;
      this.onCriticalError(error);
    },

    onInvalidCredentialIdError(error) {
      this.isInvalidCredentialId = true;
      this.onCriticalError(error);
    },

    onChangeStep(step) {
      if (typeof STEPS_TO_COMPONENT_MAP[step] === "undefined") {
        return;
      }

      this.step = step;
    },

    onSessionStarted(session) {
      this.session = session;
      this.step = EMAIL_OTP_STEP;
    },

    onSuccessOtp(tokens) {
      if (tokens !== null) {
        return this.emitAuthTokens(tokens);
      }

      this.openPhoneInput();
      window.scrollTo(0,0);
    },

    onPhoneSessionStarted() {
      this.step = PHONE_OTP_STEP;
    },

    onSuccessOtpPhone(tokens) {
      eventBus.emit(AUTHENTICATED_EVENT, {
        tokens,
      });
    },

    onBack() {
      if (!this.previous) {
        return;
      }

      this.step = this.previous;
    },

    openPhoneInput() {
      if (this.isUkUserWarning) {
        this.step = UK_WARNING_STEP;
        return;
      }
      if (this.view === "funnel") {
        this.step = PHONE_FUNNEL_STEP;
        return;
      }
      if (
        this.session.userType === UserType.CORPORATE
      ) {
        this.step = PHONE_CORPORATE_STEP;
        return;
      }

      this.step = PHONE_COMMON_STEP;
    },

    startAuthSession(email, locale = this.$i18n.locale) {
      return AuthService.startSession(
        email,
        locale,
        this.view,
        this.theme,
        this.isEmailProvided,
        this.sourceInfo
      )
        .then((session) => this.onSessionStarted(session))
        .catch((error) => {
          this.onChangeStep(this.view);
          this.onCriticalError(error);
        })
        .finally(() => {
          this.makeApplicationLoaded();
        });
    },

    emitAuthTokens(tokens) {
      eventBus.emit(AUTHENTICATED_EVENT, {
        tokens,
      });
    },

    resumeStoredSession() {
      this.session = AuthStorage.getSession();

      this.makeApplicationLoaded();

      if (this.session.hasPhoneOtpSession()) {
        return this.onPhoneSessionStarted();
      }

      if (this.session.getEmailOtpSession().isConfirmed()) {
        return this.openPhoneInput();
      }

      if (this.session.getUserType() !== UserType.PRIVATE) {
        this.userType = this.session.getUserType();
      }

      this.onSessionStarted(this.session);
    },

    async restartSessionWithLogout() {
      AuthStorage.clearStoredSession();

      if (TokenService.hasToken()) {
        await AuthService.logout();
      }

      if (this.email) {
        this.startAuthSession(this.email, this.locale);
        return;
      }

      this.makeApplicationLoaded();
    },
  },
};
</script>

<style scoped lang="scss">
.app {
  &__inner {
    padding: rem(32) 0;
  }

  &__body {
    padding: 0 rem(24) rem(40) rem(32);
  }

  @media screen and (min-width: $screen-lg-min) {
    &__body {
      padding: 0 rem(52) rem(40) rem(52);
    }
  }

  @media screen and (min-width: $screen-lg-min) {
    &__inner {
      padding: rem(28) 0;
    }
  }
}
</style>
